/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import axios, { type AxiosInstance } from 'axios';
import { BV_BASE_URL, BV_PASS_KEY } from '../constants';
import type { BVProfile, BVProfileResponse, BVProfileStats } from '../interface/bv-types';

const URL = `${BV_BASE_URL}/data/authors.json`;

type GetReviewsProfileOptions = {
  profileIDs: string[];
  stats?: BVProfileStats[];
};

const getReviewsAuthors = async ({ profileIDs, stats }: GetReviewsProfileOptions) => {
  const filter = `id:${profileIDs.join(',')}`;
  const response = await axios.get<BVProfileResponse>(URL, {
    params: {
      Filter: filter,
      Stats: stats?.join(','),
      ApiVersion: 5.4,
      PassKey: BV_PASS_KEY,
    },
  });

  return selector(response.data);
};

const selector = (data: BVProfileResponse) => {
  const mappedResults: Record<string, BVProfile> = {};
  for (const profile of data.Results) {
    mappedResults[profile.Id] = profile;
  }
  return {
    ...data,
    Results: mappedResults,
  };
};

const { useData: useReviewsAuthorsData, prefetch: prefetchReviewsAuthorsData } = createQuery(
  'reviewsAuthors',
  getReviewsAuthors
);

export const useReviewsAuthors = (options: GetReviewsProfileOptions & { enabled?: boolean }) => {
  const bvAPIEnabled = useFeatureFlag('BV_API_ENABLED') === 'true';

  return useReviewsAuthorsData({
    ...options,
    enabled: !!options.profileIDs.length && bvAPIEnabled && options.enabled,
  });
};

export const prefetchReviewsAuthors = (
  options: GetReviewsProfileOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchReviewsAuthorsData(queryClient, options, axiosInstance);
};
